import { Box, Stack } from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const SlideShowPanel = () => {
    const [slides, setSlides] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [frontImage, setFrontImage] = useState('');
    const [backImage, setBackImage] = useState('');
    const [isFrontVisible, setIsFrontVisible] = useState(true);
    const [delay, setDelay] = useState(5000); // Slide delay
    const [previousHash, setPreviousHash] = useState(null);
    const fadeDuration = 1000; // Fade duration
    const intervalRef = useRef(null);
    const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL;

    // Very fast FNV-1a hash for strings
    const hashFnv1a = (str) => {
        let hash = 0x811c9dc5;
        for (let i = 0; i < str.length; i++) {
            hash ^= str.charCodeAt(i);
            hash = (hash * 0x01000193) >>> 0; // 32-bit unsigned
        }
        return hash.toString(16);
    };

    const loadSlideshow = async () => {
        try {
            const cacheBuster = `cb=${Date.now()}`; // Or use Math.random()
            const response = await axios.get(`${S3_BASE_URL}/slideshow.json?${cacheBuster}`);
            const data = response.data;
            if (data?.slides?.length) {
                // Check if we have any changes
                const jsonString = JSON.stringify(data);
                const currentHash = hashFnv1a(jsonString);
                if (currentHash !== previousHash) {
                    setSlides(data.slides);
                    const initialUrl = `${S3_BASE_URL}/${data.slides[0].filename}`;
                    setFrontImage(initialUrl);
                    setBackImage(initialUrl);
                    setDelay(data.delay ?? delay);
                    setPreviousHash(currentHash);
                    console.log('Slideshow updated');
                }
            }
        } catch (error) {
            console.error('Failed to load slideshow:', error);
        }
    };

    useEffect(() => {
        loadSlideshow();
    }, []);

    useEffect(() => {
        if (!slides.length) return;

        intervalRef.current = setInterval(async () => {
            const nextIndex = (currentIndex + 1) % slides.length;
            const nextUrl = `${S3_BASE_URL}/${slides[nextIndex].filename}`;
            if (isFrontVisible) {
                setBackImage(nextUrl);
            } else {
                setFrontImage(nextUrl);
            }
            if (nextIndex === 0) {
                await loadSlideshow();
            }
            setIsFrontVisible(!isFrontVisible);
            setCurrentIndex(nextIndex);
        }, delay);

        return () => clearInterval(intervalRef.current);
    }, [slides, currentIndex, isFrontVisible]);

    return (
        <Box sx={{ height: '100%', width: '100%', padding: '2rem', marginTop: '1rem' }}>
            <Stack sx={{ height: '100%', width: '100%' }} spacing={1}>
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        aspectRatio: '7 / 10', // Hardcoded aspect ratio for now
                        backgroundColor: '#000',
                        overflow: 'hidden',
                    }}
                >
                    <img
                        src={frontImage}
                        alt=""
                        style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center top', // Align to top to prevent cropping important parts
                            transition: `opacity ${fadeDuration}ms ease-in-out`,
                            opacity: isFrontVisible ? 1 : 0,
                            zIndex: isFrontVisible ? 2 : 1,
                        }}
                    />
                    <img
                        src={backImage}
                        alt=""
                        style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center top', // Align to top to prevent cropping important parts
                            transition: `opacity ${fadeDuration}ms ease-in-out`,
                            opacity: isFrontVisible ? 0 : 1,
                            zIndex: isFrontVisible ? 1 : 2,
                        }}
                    />
                </Box>
            </Stack>
        </Box>
    );
};

export default SlideShowPanel;
